import { movies, type Movie } from '@/mocks/movies';

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getLatestMovies(count?: number): Movie[] {
  const sorted = [...movies].sort(
    (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime(),
  );
  return typeof count === 'number' ? sorted.slice(0, count) : sorted;
}

export function getHotMovies(count?: number): Movie[] {
  const sorted = [...movies].sort((a, b) => b.views - a.views);
  return typeof count === 'number' ? sorted.slice(0, count) : sorted;
}

export type SortKey = 'latest' | 'hot' | 'rating';

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const copy = [...list];
  if (sort === 'hot') return copy.sort((a, b) => b.views - a.views);
  if (sort === 'rating') return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort(
    (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime(),
  );
}

export interface FilterOptions {
  type?: string;
  regions?: string[];
  yearRange?: { min: number; max: number } | null;
  genres?: string[];
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { type, regions = [], yearRange = null, genres = [] } = options;
  return movies.filter((movie) => {
    if (type && type !== 'hot' && movie.type !== type) return false;
    if (regions.length > 0 && !regions.includes(movie.region)) return false;
    if (yearRange && (movie.year < yearRange.min || movie.year > yearRange.max)) return false;
    if (genres.length > 0 && !genres.some((genre) => movie.genres.includes(genre))) return false;
    return true;
  });
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.originalTitle,
      movie.synopsis,
      movie.region,
      movie.director,
      ...movie.genres,
      ...movie.actors,
      ...movie.tags,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function getRelatedMovies(movie: Movie, count = 6): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.region === movie.region) score += 3;
      if (item.type === movie.type) score += 2;
      score += item.genres.filter((genre) => movie.genres.includes(genre)).length * 2;
      score += item.tags.filter((tag) => movie.tags.includes(tag)).length;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, count).map((entry) => entry.item);
}